import { ref, computed } from 'vue';
import axios from 'axios';
import url from '@/url';

/**
 * Permission Composable
 * Manages user permissions state and provides methods to check permissions
 */
export function usePermissions() {
  const permissions = ref({});
  const loading = ref(false);
  const error = ref(null);

  /**
   * Fetch user permissions from API
   */
  const fetchUserPermissions = async () => {
    loading.value = true;
    error.value = null;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        process.env.VUE_APP_SERVICE_URL + url.permissions_me,
        {
          headers: {
            'X-Authorization': token,
          },
        }
      );

      if (response.data && response.data.result && response.data.result.data) {
        permissions.value = response.data.result.data.permissions || {};
      } else if (response.data && response.data.data) {
        permissions.value = response.data.data.permissions || {};
      }
    } catch (err) {
      console.error('Error fetching permissions:', err);
      error.value = err.message;
      permissions.value = {};
    } finally {
      loading.value = false;
    }
  };

  /**
   * Check if user has permission for specific page and action
   * ⚠️ Backward Compatibility: If no permissions loaded, return true (allow access)
   * 
   * @param {string} pageCode - Page code (e.g., 'students', 'teachers')
   * @param {string} action - Action (e.g., 'read', 'create', 'update', 'delete')
   * @returns {boolean}
   */
  const hasPermission = (pageCode, action) => {
    const userRole = localStorage.getItem('userRole');
    
    // Super Admin has all permissions
    if (userRole === 'SUPER_ADMIN') {
      return true;
    }

    // ⚠️ Backward Compatibility: If no permissions loaded, allow access
    // This means the permissions system is optional and doesn't affect existing users
    if (!permissions.value || Object.keys(permissions.value).length === 0) {
      return true; // Allow access if permissions system not configured
    }

    const pagePermissions = permissions.value[pageCode];
    
    // ⚠️ If page not in permissions, allow access (backward compatibility)
    if (!pagePermissions) {
      return true;
    }

    // If permission is explicitly set to false, deny access
    // Otherwise, allow access (backward compatibility)
    return pagePermissions[action] !== false;
  };

  /**
   * Check if user can access a page (has read permission)
   * @param {string} pageCode
   * @returns {boolean}
   */
  const canAccessPage = (pageCode) => {
    return hasPermission(pageCode, 'read');
  };

  /**
   * Check if user can perform specific action
   * @param {string} pageCode
   * @param {string} action
   * @returns {boolean}
   */
  const canPerformAction = (pageCode, action) => {
    return hasPermission(pageCode, action);
  };

  /**
   * Get all permissions for a specific page
   * @param {string} pageCode
   * @returns {object|null}
   */
  const getPagePermissions = (pageCode) => {
    return permissions.value[pageCode] || null;
  };

  /**
   * Check if permissions are loaded
   */
  const isLoaded = computed(() => {
    return Object.keys(permissions.value).length > 0;
  });

  return {
    permissions,
    loading,
    error,
    isLoaded,
    fetchUserPermissions,
    hasPermission,
    canAccessPage,
    canPerformAction,
    getPagePermissions,
  };
}

